import { METRIC_DATA_TYPES } from '@/common/metrics-card/constants';
import { i18n_t as $t } from '@/utils/i18n';
import { EMAIL_EVENT_TYPES } from '@pendo/middleware';

const { SENT, DELIVERED, OPEN, CLICK, BOUNCE, UNSUBSCRIBE, COMPLAINT } = EMAIL_EVENT_TYPES;

export const CLICK_TO_OPEN = 'click-to-open';

const { PERCENTAGE, NUMBER } = METRIC_DATA_TYPES;

export const EMAIL_METRICS_SETTINGS = () => ({
    [SENT]: {
        label: $t('emailConstsUtils.Total sends'),
        type: NUMBER,
        tooltip: {
            singleEmail: $t('emailConstsUtils.The number of sent emails'),
            totalEmails: $t('emailConstsUtils.The total number of sent emails'),
        },
    },
    [DELIVERED]: {
        label: $t('emailConstsUtils.Delivery rate'),
        type: PERCENTAGE,
        tooltip: {
            singleEmail: $t(
                'emailConstsUtils.The_percentage_of_sent_emails_that_were_successfully_delivered_to_recipients_inboxes',
            ),
            totalEmails: $t(
                'emailConstsUtils.The_percentage_of_sent_emails_that_were_successfully_delivered_to_recipients_inboxes',
            ),
        },
    },
    [OPEN]: {
        label: $t('emailConstsUtils.Unique open rate'),
        type: PERCENTAGE,
        tooltip: {
            singleEmail: $t('emailConstsUtils.The percentage of recipients who opened your email at least once'),
            totalEmails: $t(
                'emailConstsUtils.The percentage of recipients who opened one of your emails at least once',
            ),
        },
    },
    [CLICK]: {
        label: $t('emailConstsUtils.Unique click rate'),
        type: PERCENTAGE,
        tooltip: {
            singleEmail: $t(
                'emailConstsUtils.The percentage of recipients who clicked a link in your email at least once',
            ),
            totalEmails: $t(
                'emailConstsUtils.The percentage of recipients who clicked a link in one of your emails at least once',
            ),
        },
    },
    [CLICK_TO_OPEN]: {
        label: $t('emailConstsUtils.Click-to-open rate'),
        type: PERCENTAGE,
        description: $t('emailConstsUtils.No open records'),
        eventTypeValue: CLICK,
        eventTypeTotal: OPEN,
        tooltip: {
            singleEmail: $t('emailConstsUtils.The percentage of unique clicks out of unique opens'),
            totalEmails: $t('emailConstsUtils.The percentage of unique clicks out of unique opens'),
        },
    },
    [BOUNCE]: {
        label: $t('emailConstsUtils.Bounce rate'),
        type: PERCENTAGE,
        tooltip: {
            singleEmail: $t(
                'emailConstsUtils.The percentage of emails that werent delivered to recipients inboxes due to issues like invalid addresses or server problems',
            ),
            totalEmails: $t(
                'emailConstsUtils.The percentage of emails that werent delivered to recipients inboxes due to issues like invalid addresses or server problems',
            ),
        },
    },
    [UNSUBSCRIBE]: {
        label: $t('emailConstsUtils.Unsubscribe rate'),
        type: PERCENTAGE,
        tooltip: {
            singleEmail: $t(
                'emailConstsUtils.The_percentage_of_recipients_who_opted_out_of_your_mailing_list_by_selecting_the_unsubscribe_link_in_the_body_of_the_email_or_email_header',
            ),
            totalEmails: $t(
                'emailConstsUtils.The_percentage_of_recipients_who_opted_out_of_your_mailing_list_by_selecting_the_unsubscribe_link_in_the_body_of_the_email_or_email_header',
            ),
        },
    },
    [COMPLAINT]: {
        label: $t('emailConstsUtils.Spam rate'),
        type: PERCENTAGE,
        tooltip: {
            singleEmail: $t('emailConstsUtils.The percentage of recipients who marked your email as spam'),
            totalEmails: $t('emailConstsUtils.The percentage of recipients who marked one of your emails as spam'),
        },
    },
});

export const EMAIL_STATES = {
    DRAFT: 'draft',
    REVIEW: 'review',
    SCHEDULED: 'scheduled',
    PUBLIC: 'public',
    SENT: 'sent',
    DISABLED: 'disabled',
};

/* eslint-disable internal-rules/no-hardcoded-colors */
export const STATUS_COLOR_MAP = {
    [EMAIL_STATES.DRAFT]: 'blue',
    [EMAIL_STATES.REVIEW]: 'teal',
    [EMAIL_STATES.SCHEDULED]: 'orange',
    [EMAIL_STATES.PUBLIC]: 'green',
    [EMAIL_STATES.SENT]: 'gray',
    [EMAIL_STATES.DISABLED]: 'gray',
};

export const EMAIL_METRICS_COUNTING_MODES = Object.freeze({
    EVENTS_IN_DATE_RANGE: 'eventsInDateRange',
    DELIVERED_COHORT: 'deliveredCohort',
});

export const EMAIL_CHANNEL = 'email';

export const EMAIL_CONTENT_SOURCE = {
    HTML: 'html',
    BLOCKS: 'blocks',
};

export const EMAIL_TYPE_CONFIG = Object.freeze([
    {
        id: EMAIL_CONTENT_SOURCE.HTML,
        titleKey: 'emails.HTML_email',
        descriptionKey: 'emails.HTML_email_description',
        icon: 'code',
        iconBackgroundColor: 'var(--status-color-map-blue-background)',
        iconColor: 'var(--status-color-map-blue-primary)',
        analyticsTag: 'email-list-create-tile-html',
        showNewTag: true,
    },
    {
        id: EMAIL_CONTENT_SOURCE.BLOCKS,
        titleKey: 'emails.Template',
        descriptionKey: 'emails.Template_description',
        icon: 'layout',
        iconBackgroundColor: 'var(--status-color-map-green-background)',
        iconColor: 'var(--status-color-map-green-primary)',
        analyticsTag: 'email-list-create-tile-template',
    },
]);

export const UNSUBSCRIBE_TOKEN = '{{ UnsubscribeURI }}';